<template>
  <div>
    <RevForm
      class="mb-16 flex"
      :initial-values="filtersInitialValues"
      :validate="validate()"
      @submit="handleSubmit"
    >
      <template #default="{ errors, values, reset }: any">
        <div class="flex flex-1 flex-col gap-16">
          <div class="grid gap-16 md:grid-cols-3 lg:grid-cols-5">
            <RevInputMultiSelect
              id="status"
              v-model="values.status"
              class="z-20"
              :disabled="false"
              :label="i18n(translations.status)"
              :options="statusFilterOption"
            />
            <RevInputMultiSelect
              id="taskTypes"
              v-model="values.taskTypes"
              class="z-20"
              :disabled="false"
              :label="i18n(translations.task)"
              :options="useTypeOptions()"
            />
            <RevInputText
              id="orderId"
              v-model="values.orderId"
              :disabled="false"
              :error="errors.orderId"
              :has-cleaning-icon="false"
              :label="i18n(translations.orderId)"
            />
            <RevInputText
              id="customerFirstName"
              v-model="values.customerFirstName"
              :has-cleaning-icon="false"
              :label="i18n(translations.customerFirstName)"
            />
            <RevInputText
              id="customerLastName"
              v-model="values.customerLastName"
              :has-cleaning-icon="false"
              :label="i18n(translations.customerLastName)"
            />
          </div>
          <div
            v-if="shouldShowAllFilters"
            class="grid gap-16 md:grid-cols-3 lg:grid-cols-5"
          >
            <RevInputMultiSelect
              id="market"
              v-model="values.markets"
              class="z-10"
              :disabled="false"
              :label="i18n(translations.market)"
              :options="marketOptions"
            />
            <RevInputText
              id="sku"
              v-model="values.sku"
              :has-cleaning-icon="false"
              :label="i18n(translations.sku)"
            />
          </div>
        </div>
        <div>
          <RevButton
            class="ml-32"
            data-qa="customer-care-apply-filters-button"
            type="submit"
            variant="primary"
            >{{ i18n(translations.applyFilters) }}</RevButton
          >

          <RevLink
            class="ml-16"
            data-qa="customer-care-reset-filters-button"
            @click="resetFilters(reset)"
            >{{ i18n(translations.reset) }}</RevLink
          >
        </div>
      </template>
    </RevForm>

    <RevButton
      v-if="shouldShowAllFilters"
      data-qa="customer-care-see-less-filters-button"
      size="small"
      variant="secondary"
      @click="toggleMoreFilters"
      >{{ i18n(translations.seeLessFilters) }}</RevButton
    >

    <RevButton
      v-if="!shouldShowAllFilters"
      data-qa="customer-care-see-more-filters-button"
      size="small"
      variant="secondary"
      @click="toggleMoreFilters"
      >{{ i18n(translations.seeMoreFilters) }}</RevButton
    >
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, matchingRegExp } from '@ds/components/Form'
import { RevInputMultiSelect } from '@ds/components/InputMultiSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'

import type {
  Filter,
  FilterOptionItem,
} from '~/api/seller-after-sales/types/requests.types'
import { useMarketOptions } from '~/scopes/customer-care/pages/customer-care/composables/useMarketOptions'
import { useTypeOptions } from '~/scopes/customer-care/pages/customer-care/composables/useTypeOptions'

import {
  EMPTY_FILTERS_VALUES,
  VALID_ORDER_NUMBER_REGEXP,
} from '../Filters.constants'
import translations from '../Filters.translations'

const i18n = useI18n()
const tracking = useTracking()
const emit = defineEmits(['apply-filters'])
const marketOptions = useMarketOptions()

const props = defineProps<{
  pageType: string
  statusFilterOption: Array<FilterOptionItem>
  initialValues: Filter
}>()

const shouldShowAllFilters = ref(
  !(isEmpty(props.initialValues.markets) && isEmpty(props.initialValues.sku)),
)
const filtersInitialValues = reactive(props.initialValues)

function validate() {
  return makeValidate<typeof props.initialValues>({
    orderId: matchingRegExp(
      VALID_ORDER_NUMBER_REGEXP,
      i18n(translations.orderIdWrongFormat),
    ),
  })
}

function toggleMoreFilters() {
  shouldShowAllFilters.value = !shouldShowAllFilters.value
}
function handleSubmit(values: typeof props.initialValues) {
  const fieldsToTrack = {
    status_filter: values.status,
    task_type_filter: values.taskTypes,
    order_id_filter: values.orderId,
    customer_fist_name_filter: values.customerFirstName,
    customer_last_name_filter: values.customerLastName,
    market_filter: values.markets,
    sku_filter: values.sku,
  }

  tracking.trackClick({
    name: 'filters_submited',
    page_type: props.pageType,
    ...fieldsToTrack,
  })

  emit('apply-filters', values)
}
function resetFilters(
  resetForm: () => { isValid: boolean; values: typeof props.initialValues },
) {
  Object.assign(filtersInitialValues, EMPTY_FILTERS_VALUES)

  resetForm()
  emit('apply-filters', EMPTY_FILTERS_VALUES)
}
</script>
